import { useMemo } from 'react';
import { useBetslipContext } from '@/features/betslip/contexts/betslip-context';
import { useSiteStore } from '@/store/site';
import { useBetslipStore } from '@/store/betslip';
import { useAccountInfo } from '../query/account';
import { useCwcJackpotSetting } from '../query/cwc-jackpot/use-cwc-jackpot-setting';

export const TICKET_STATES = {
	NO_STAKE: 'NO_STAKE',
	AWAY_COUNT: 'AWAY_COUNT',
	REACHED: 'REACHED',
	NO_AMOUNT: 'NO_AMOUNT'
} as const;

export interface TicketEligibilityInfo {
	isEligible: boolean;
	targetAmount: number;
	currentStake: number;
	awayCount: number;
	ticketState: typeof TICKET_STATES[keyof typeof TICKET_STATES];
	currencyName: string;
}

const getTicketState = (currentStake: number, awayCount: number): typeof TICKET_STATES[keyof typeof TICKET_STATES] => {
	if (!currentStake) return TICKET_STATES.NO_STAKE;
	if (awayCount > 0) return TICKET_STATES.AWAY_COUNT;
	if (awayCount === 0) return TICKET_STATES.REACHED;
	return TICKET_STATES.NO_AMOUNT;
};

export const useCwcJackpotTicketEligibility = ({
	SportType,
	LeagueId,
	BetTypeId,
	stake,
}: {
	SportType: number;
	LeagueId: number;
	BetTypeId: number;
	stake: number;
}): TicketEligibilityInfo => {
	const { data: cwcJackpotSetting, isCwcJackpotSupportedByBetType } = useCwcJackpotSetting();
	const { user } = useAccountInfo();
	const { currencyName } = useBetslipContext();
	const [isParlayMode] = useSiteStore(state => [state.isParlayMode]);
	const [betslipTab, useSabaCoin] = useBetslipStore(state => [state.betslipTab, state.useSabaCoin]);
	const isParlayTab = betslipTab === 'parlay';

	const isEligibleLeagueAndBetType = useMemo(() => {
		return isCwcJackpotSupportedByBetType(SportType, LeagueId, BetTypeId);
	}, [cwcJackpotSetting?.LeagueIds]);
  
	const currencySetting = useMemo(() => {
		if (!cwcJackpotSetting || !user || !isEligibleLeagueAndBetType) return null;
    
		const userCurrencyId = user.CurrId;
		return cwcJackpotSetting.CurrencySettings.find(
			setting => setting.CurrencyId === userCurrencyId
		);
	}, [cwcJackpotSetting, user, isEligibleLeagueAndBetType]);

	const targetAmount = useMemo(() => 
		currencySetting?.TargetAmount ?? 0
	, [currencySetting]);

	const awayCount = useMemo(() => {
		if (!currencySetting) return 0;
    
		const currentStake = stake || 0;
		const awayCount = Math.max(0, targetAmount - currentStake);
		return Math.ceil(awayCount);
	}, [currencySetting, targetAmount, stake]);

	const isEligible = useMemo(() => {
		if (!isEligibleLeagueAndBetType || isParlayMode || isParlayTab || useSabaCoin) return false;
		return true;
	}, [isEligibleLeagueAndBetType, isParlayMode, isParlayTab, useSabaCoin]);

	const ticketState = getTicketState(stake, awayCount);

	return {
		isEligible,
		targetAmount,
		currentStake: stake || 0,
		awayCount,
		currencyName,
		ticketState
	};
}; 